import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../config/api';
import { useNavigate, useLocation } from 'react-router-dom';
import ReviewForm from '../components/ReviewForm';
import Chat from '../components/Chat';
import Icon from '../components/Icon';
import NotificationsPanel, { useNotifications } from '../components/NotificationsPanel';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { toast } from '../components/ToastProvider';
import '../styles/Dashboard.css';
import '../styles/B2BTrainings.css';

const ClientDashboard = () => {
  const { user } = useAuth();
  const { unread: notifUnread } = useNotifications(user?.id);
  const { unreadCount: chatUnread, unreadBreakdown } = useUnreadMessages(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (location.state?.openTab) {
      setActiveTab(location.state.openTab);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail.tab);
    window.addEventListener('hairkus-open-tab', handler);
    return () => window.removeEventListener('hairkus-open-tab', handler);
  }, []);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedSalonChat, setSelectedSalonChat] = useState(null);

  useEffect(() => {
    const handleChatDeleted = () => {
      setSelectedSalonChat(null);
    };
    window.addEventListener('chat-deleted', handleChatDeleted);
    return () => window.removeEventListener('chat-deleted', handleChatDeleted);
  }, []);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);

  const normalizeBooking = (b) => ({
    ...b,
    date:     (b.booking_date || b.date || '').slice(0, 10),
    time:     (b.booking_time || b.time || '').slice(0, 5),
    total:    Number(b.total) || 0,
    salon:    b.salon || { name: b.salon_name, address: b.salon_address },
    employee: b.employee || (b.employee_name ? { name: b.employee_name } : null),
    services: b.services || (b.service_names?.filter(Boolean).map(name => ({ name })) || []),
    products: b.products || [],
  });

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const [bData, rData] = await Promise.all([
        apiFetch('/bookings'),
        apiFetch('/reviews?userId=' + user.id),
      ]);
      setBookings((bData || []).map(normalizeBooking));
      setReviews(rData || []);
    } catch {
    }

    try {
      const fData = await apiFetch('/b2b/favorites');
      setFavorites(fData || []);
    } catch {
      setFavorites([]);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCancelBooking = async (bookingId) => {
    try {
      await apiFetch(`/bookings/${bookingId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'cancelled' }),
      });
      setCancelConfirmId(null);
      loadData();
      toast.info('Rezerwacja została anulowana.');
    } catch {
      toast.error('Błąd podczas anulowania rezerwacji');
    }
  };

  const handleReviewClick = (booking) => {
    const existingReview = reviews.find(r => r.salon_id === booking.salon_id);
    if (existingReview) {
      toast.info('Już wystawiłeś ocenę dla tego salonu. Możesz ją zaktualizować.');
    }
    setSelectedBooking(booking);
    setShowReviewForm(true);
  };

  const handleReviewSubmit = async (review) => {
    try {
      const response = await apiFetch('/reviews', {
        method: 'POST',
        body: JSON.stringify(review),
      });
      setShowReviewForm(false);
      setSelectedBooking(null);
      loadData();
      
      if (response.updated) {
        toast.success('Opinia zaktualizowana!');
      } else {
        toast.success('Dziękujemy za opinię!');
      }
    } catch {
      toast.error('Błąd podczas wysyłania opinii');
    }
  };

  const hasReview = (bookingId) => reviews.some(r => r.booking_id === bookingId);

  const getStatusBadge = (status) => {
    const map = {
      pending:   { text: 'Oczekuje',     color: '#f59e0b', icon: 'clock'     },
      confirmed: { text: 'Potwierdzona', color: '#10b981', icon: 'confirmed' },
      rejected:  { text: 'Odrzucona',   color: '#ef4444', icon: 'rejected'  },
      completed: { text: 'Zrealizowana', color: '#6366f1', icon: 'check'     },
      cancelled: { text: 'Anulowana',   color: '#9ca3af', icon: 'cancelled' },
    };
    const b = map[status] || map.pending;
    return (
      <span className="status-badge" style={{ background: b.color + '20', color: b.color }}>
        <Icon name={b.icon} style={{marginRight:'5px'}} />{b.text}
      </span>
    );
  };

  const uniqueSalons = [...new Map(
    bookings.filter(b => b.salon).map(b => [
      b.salon.name, 
      { ...b.salon, id: b.salon_id || b.salon.id }
    ])
  ).values()];

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">

        <div className="dashboard-header">
          <h1>Panel Klienta</h1>
          <p>Witaj, {user?.firstName}! </p>
        </div>

        {/* Statystyki */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon"><Icon name="stats" style={{marginRight:'7px'}} /></div>
            <div className="stat-content"><h3>{bookings.length}</h3><p>Wszystkie</p></div>
          </div>
          <div className="stat-card highlight-pending">
            <div className="stat-icon"><Icon name="pending" color="#f59e0b" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{bookings.filter(b => b.status === 'pending').length}</h3><p>Oczekujące</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Icon name="confirmed" color="#10b981" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{bookings.filter(b => b.status === 'confirmed').length}</h3><p>Potwierdzone</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Icon name="check" style={{marginRight:"4px"}} /></div>
            <div className="stat-content"><h3>{bookings.filter(b => b.status === 'completed').length}</h3><p>Zrealizowane</p></div>
          </div>
        </div>

        {/* Zakładki */}
        <div className="admin-tabs">
          <button className={`admin-tab ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
            <Icon name="calendar" style={{marginRight:'7px'}} />Moje rezerwacje
          </button>
          <button className={`admin-tab ${activeTab === 'favorites' ? 'active' : ''}`} onClick={() => setActiveTab('favorites')}>
            <Icon name="heart" color="#ef4444" style={{marginRight:'7px'}} />Ulubione{favorites.length > 0 ? ` (${favorites.length})` : ''}
          </button>
          <button className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')} style={{position:'relative'}}>
            <Icon name="chat" style={{marginRight:'7px'}} />Wiadomości
            {activeTab !== 'messages' && chatUnread > 0 && (
              <span className="tab-unread-badge">{chatUnread > 9 ? '9+' : chatUnread}</span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')} style={{position:'relative'}}>
            <Icon name="notification" style={{marginRight:'7px'}} />Powiadomienia
            {activeTab !== 'notifications' && notifUnread > 0 && (
              <span className="tab-unread-badge">{notifUnread > 9 ? '9+' : notifUnread}</span>
            )}
          </button>
        </div>

        <div className="dashboard-content-single">

          {/* Rezerwacje */}
          {activeTab === 'bookings' && (
            <>
              <div className="dashboard-card-full">
                <div className="card-header-with-button">
                  <h2><Icon name="calendar" style={{marginRight:'7px'}} /> Historia rezerwacji</h2>
                  <button className="cta-button" onClick={() => navigate('/')}>+ Nowa rezerwacja</button>
                </div>

                {bookings.length > 0 ? (
                  <div className="table-container">
                    <table className="bookings-table">
                      <thead>
                        <tr>
                          <th>Salon</th>
                          <th>Data</th>
                          <th>Godzina</th>
                          <th>Pracownik</th>
                          <th>Usługi</th>
                          <th>Produkty</th>
                          <th>Kwota</th>
                          <th>Status</th>
                          <th>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map(booking => (
                          <tr key={booking.id}>
                            <td>
                              <div className="salon-cell">
                                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                  <Icon name="location" color="#1e3c72" size="sm" />
                                  <strong>{booking.salon?.name}</strong>
                                </div>
                                <span className="address-small">{booking.salon?.address}</span>
                              </div>
                            </td>
                            <td><strong>{booking.date}</strong></td>
                            <td><span className="time-badge"><Icon name="clock" style={{marginRight:"4px"}} /> {booking.time}</span></td>
                            <td>
                              <div className="employee-cell">
                                <div className="employee-mini-avatar">
                                  {booking.employee?.name?.split(' ').map(n => n[0]).join('')}
                                </div>
                                <span>{booking.employee?.name}</span>
                              </div>
                            </td>
                            <td>
                              <div className="services-cell">
                                {booking.services?.map((s, idx) => (
                                  <div key={idx} className="service-tag">{s.name}</div>
                                ))}
                              </div>
                            </td>
                            <td>
                              {booking.products?.length > 0 ? (
                                <div className="products-cell">
                                  {booking.products.map((p, idx) => (
                                    <div key={idx} className="product-tag"> {p.name}</div>
                                  ))}
                                </div>
                              ) : <span className="no-products">-</span>}
                            </td>
                            <td className="price-cell">
                              <strong>{booking.total} zł</strong>
                              {booking.productsTotal > 0 && (
                                <div className="price-breakdown">
                                  <small>Usługi: {booking.servicesTotal} zł</small>
                                  <small>Produkty: {booking.productsTotal} zł</small>
                                </div>
                              )}
                            </td>
                            <td>{getStatusBadge(booking.status)}</td>
                            <td>
                              {booking.status === 'completed' && !hasReview(booking.id) && (
                                <button className="review-btn-small" onClick={() => handleReviewClick(booking)}>
                                  <Icon name="star" color="#f59e0b" style={{marginRight:'5px'}} /> Oceń
                                </button>
                              )}
                              {hasReview(booking.id) && (
                                <span className="reviewed-badge"><Icon name="check" /> Oceniono</span>
                              )}
                              {booking.status === 'pending' && (
                                <button className="cancel-btn-small" onClick={() => setCancelConfirmId(booking.id)}>
                                  <Icon name="cancelled" color="white" style={{marginRight:'5px'}} /> Anuluj
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="calendar" style={{marginRight:'7px'}} /></div>
                    <h3>Brak rezerwacji</h3>
                    <p>Nie masz jeszcze żadnych rezerwacji</p>
                    <button className="cta-button-large" onClick={() => navigate('/')}>
                      Zarezerwuj pierwszą wizytę
                    </button>
                  </div>
                )}
              </div>

              <div className="features-grid">
                <div className="feature-card">
                  <h3><Icon name="star" color="#f59e0b" style={{marginRight:'5px'}} /> Twoje opinie ({reviews.length})</h3>
                  <p>Oceny wystawione salonom</p>
                  {reviews.length > 0 && (
                    <div className="reviews-preview">
                      {reviews.slice(0, 3).map(review => (
                        <div key={review.id} className="review-preview-item">
                          <strong>{review.salon_name || review.salonName}</strong>
                          <span className="stars-small">
                            {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="feature-card">
                  <h3><Icon name="user" style={{marginRight:'7px'}} /> Edycja profilu</h3>
                  <p>Zmień imię, telefon lub hasło</p>
                  <button className="cta-button" style={{marginTop:'12px'}} onClick={() => navigate('/profile')}>
                    Edytuj profil →
                  </button>
                </div>
              </div>
            </>
          )}


          {/* Ulubione */}
          {activeTab === 'favorites' && (
            <div className="favorites-section">
              {/* Nagłówek */}
              <div className="favorites-header">
                <div className="favorites-header-left">
                  <div className="favorites-header-icon">
                    <Icon name="heart" color="white" size="xl" />
                  </div>
                  <div>
                    <h2>Ulubione salony</h2>
                    <p>{favorites.length > 0 ? `${favorites.length} zapisanych salonów` : 'Twoja lista ulubionych'}</p>
                  </div>
                </div>
                <button className="favorites-search-btn" onClick={() => navigate('/')}>
                  <Icon name="search" style={{marginRight:'8px'}} />Odkryj nowe salony
                </button>
              </div>

              {favorites.length > 0 ? (
                <div className="favorites-grid">
                  {favorites.map(fav => {
                    const salonName = fav.salon_name || fav.name;
                    const salonBookings = bookings.filter(b => b.salon_name === salonName);
                    const lastBooking = salonBookings[0];
                    const salonReviews = reviews.filter(r => r.salon_name === salonName);
                    const avgRating = salonReviews.length ? (salonReviews.reduce((s,r)=>s+r.rating,0)/salonReviews.length).toFixed(1) : null;
                    return (
                      <div key={(fav.salon_name || fav.name)} className="fav-card">
                        {/* Karta salonu */}
                        <div className="fav-card-image">
                          {fav.photo && fav.photo.trim() !== '' ? (
                            <>
                              <img 
                                src={fav.photo} 
                                alt={fav.salon_name || fav.name} 
                                style={{width:'100%',height:'100%',objectFit:'cover'}}
                                onError={(e) => {
                                  console.error('Image load error for:', fav.salon_name, fav.photo?.substring(0, 100));
                                  e.target.style.display = 'none';
                                  e.target.parentElement.querySelector('.fav-card-placeholder').style.display = 'flex';
                                }}
                              />
                              <div className="fav-card-placeholder" style={{display:'none'}}>
                                <Icon name="scissors" color="rgba(255,255,255,0.6)" size="3x" />
                              </div>
                            </>
                          ) : (
                            <div className="fav-card-placeholder" style={{background:'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
                              <div style={{textAlign:'center',color:'#fff'}}>
                                <Icon name="scissors" color="rgba(255,255,255,0.8)" size="3x" style={{marginBottom:'10px'}} />
                                <div style={{fontSize:'0.9em',fontWeight:'600',letterSpacing:'1px'}}>{(fav.salon_name || fav.name)?.substring(0, 20)}</div>
                              </div>
                            </div>
                          )}
                          <div className="fav-card-overlay" />
                          {/* Usuń */}
                          <button className="fav-remove-btn"
                            onClick={async () => {
                              const name = fav.salon_name || fav.name;
                              try {
                                await apiFetch(`/b2b/favorites/${encodeURIComponent(name)}`, { method: 'DELETE' });
                                setFavorites(prev => prev.filter(f => (f.salon_name || f.name) !== name));
                                toast.info('Usunięto z ulubionych');
                              } catch { toast.error('Błąd'); }
                            }}
                            title="Usuń z ulubionych">
                            <Icon name="heart" color="#ef4444" />
                          </button>
                          {/* Ocena */}
                          {avgRating && (
                            <div className="fav-rating-badge">
                              <Icon name="star" color="#f59e0b" style={{marginRight:'4px'}} />{avgRating}
                            </div>
                          )}
                        </div>

                        {/* Treść karty */}
                        <div className="fav-card-body">
                          <h3 className="fav-card-name">{fav.salon_name || fav.name}</h3>
                          <p className="fav-card-address">
                            <Icon name="location" color="#1e3c72" style={{marginRight:'6px'}} />
                            {fav.salon_address || fav.address}
                          </p>
                          {fav.city && (
                            <p className="fav-card-city">
                              <Icon name="map" color="#9ca3af" style={{marginRight:'6px',fontSize:'0.85em'}} />
                              {fav.salon_city || fav.city}
                            </p>
                          )}

                          {/* Statystyki */}
                          <div className="fav-card-stats">
                            <div className="fav-stat">
                              <Icon name="calendar" color="#1e3c72" />
                              <div>
                                <strong>{salonBookings.length}</strong>
                                <span>Wizyt</span>
                              </div>
                            </div>
                            <div className="fav-stat">
                              <Icon name="star" color="#f59e0b" />
                              <div>
                                <strong>{avgRating || '—'}</strong>
                                <span>Ocena</span>
                              </div>
                            </div>
                            <div className="fav-stat">
                              <Icon name="review" color="#8b5cf6" />
                              <div>
                                <strong>{salonReviews.length}</strong>
                                <span>Opinii</span>
                              </div>
                            </div>
                          </div>

                          {/* Ostatnia rezerwacja */}
                          {lastBooking && (
                            <div className="fav-last-visit">
                              <Icon name="clock" color="#9ca3af" style={{marginRight:'6px',fontSize:'0.85em'}} />
                              Ostatnia wizyta:&nbsp;<strong>{lastBooking.date}</strong>
                            </div>
                          )}
                        </div>

                        {/* Stopka karty */}
                        <div className="fav-card-footer">
                          <button className="fav-book-btn"
                            onClick={() => {
                              const salonObj = {
                                id: fav.salon_id || fav.id,
                                name: fav.salon_name || fav.name,
                                address: fav.salon_address || fav.address,
                                city: fav.salon_city || fav.city,
                                rating: avgRating || 0,
                              };
                              const userGender = localStorage.getItem('userGenderPreference') || 'all';
                              navigate(`/booking/${encodeURIComponent(salonObj.name)}`, {
                                state: { salon: salonObj, gender: userGender }
                              });
                            }}>
                            <Icon name="calendar-day" style={{marginRight:'7px'}} />Zarezerwuj wizytę
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="favorites-empty">
                  <div className="favorites-empty-hearts">
                    <Icon name="heart-empty" color="#f9a8d4" size="3x" />
                  </div>
                  <h3>Brak ulubionych salonów</h3>
                  <p>Dodaj salony do ulubionych klikając ikonę serca podczas wyszukiwania</p>
                  <div className="favorites-empty-steps">
                    <div className="fav-step"><span>1</span>Wyszukaj salon</div>
                    <Icon name="forward" color="#ccc" />
                    <div className="fav-step"><span>2</span>Kliknij serce</div>
                    <Icon name="forward" color="#ccc" />
                    <div className="fav-step"><span>3</span>Tutaj pojawi się</div>
                  </div>
                  <button className="favorites-search-btn" onClick={() => navigate('/')}>
                    <Icon name="search" style={{marginRight:'8px'}} />Szukaj salonów
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Powiadomienia */}
          {activeTab === 'notifications' && (
            <div className="dashboard-card-full">
              <NotificationsPanel user={user} />
            </div>
          )}

          {/* Wiadomości */}
          {activeTab === 'messages' && (
            <div className="dashboard-card-full">
              <h2><Icon name="chat" style={{marginRight:'7px'}} /> Napisz do salonu</h2>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Masz pytanie do salonu? Napisz bezpośrednio tutaj.
              </p>
              <div className="chat-tabs">
                {uniqueSalons.map((salon, idx) => {
                  const isActive = selectedSalonChat?.name === salon.name;
                  const convKey = `client_${user?.id}_salon_${salon.name.replace(/\s+/g, '_')}`;
                  const unread = unreadBreakdown[convKey] || 0;
                  
                  return (
                    <button
                      key={idx}
                      className={`chat-tab ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedSalonChat(salon)}
                      style={{position:'relative', fontWeight: unread > 0 ? 700 : 400}}
                    >
                      <Icon name="scissors" style={{marginRight:'7px'}} /> {salon.name}
                      {unread > 0 && !isActive && (
                        <span className="chat-unread-badge">{unread}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedSalonChat ? (
                <Chat
                  conversationId={`client_${user?.id}_salon_${selectedSalonChat.name.replace(/\s+/g, '_')}`}
                  recipientName={selectedSalonChat.name}
                  recipientRole="Salon"
                />
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="chat" style={{marginRight:'7px'}} /></div>
                  <h3>Wybierz salon</h3>
                  <p>Możesz pisać do salonów, w których złożyłeś rezerwację</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* POTWIERDZENIE ANULOWANIA */}
      {cancelConfirmId && (
        <div className="chat-modal-overlay" onClick={() => setCancelConfirmId(null)}>
          <div className="chat-modal" onClick={e => e.stopPropagation()}>
            <h3>Anulować rezerwację?</h3>
            <p>Rezerwacja zostanie anulowana. Tej operacji nie można cofnąć.</p>
            <div className="chat-modal-actions">
              <button className="chat-modal-cancel" onClick={() => setCancelConfirmId(null)}>Zostaw</button>
              <button className="chat-modal-confirm" style={{background:'#ef4444'}}
                onClick={() => handleCancelBooking(cancelConfirmId)}>
                <Icon name="cancelled" color="white" style={{marginRight:'5px'}} /> Anuluj rezerwację
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FORMULARZ OPINII */}
      {showReviewForm && selectedBooking && (
        <ReviewForm
          booking={selectedBooking}
          onSubmit={handleReviewSubmit}
          onCancel={() => {
            setShowReviewForm(false);
            setSelectedBooking(null);
          }}
        />
      )}
    </div>
  );
};

export default ClientDashboard;